import { captureException, withScope } from '@sentry/nextjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useCallback, useEffect } from 'react';
import { getUserDataFromMemberCookie } from '../../functions/utils/userUtils';
import { TRouteErrorCallback, useStatusCode } from '../../hooks/useStatusCode';
import { DEFAULT_STATUS_CODE, setUserId, trackPageview } from './googleHelpers';

type THitType = 'event' | 'pageview' | 'set' | 'create';
declare global {
  interface Window {
    ga: {
      (
        gaType: 'send',
        sendData: ISendEventData | ISendPageviewData | ISendUserData
      ): void;
      (gaType: THitType, ...rest: any): void;
      q: {
        push: (...args: any) => void;
      };
      l: number;
    };
  }
}

interface ISendData {
  hitType: THitType;
}
interface ISendEventData extends ISendData {
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string;
  eventValue?: number;
}
interface ISendPageviewData extends ISendData {
  title?: string;
  location: string;
}
interface ISendUserData extends ISendData {
  userId: string;
}

const GA_MEASUREMENT_ID = 'UA-30954-1';
const TRACKING_ENABLED_QUERY_PARAM_VALUE = '1';

interface IGoogleAnalyticsProps {
  trackingPath: string;
  initialStatusCode?: number;
}

export const GoogleAnalytics: React.FC<IGoogleAnalyticsProps> = ({
  trackingPath,
  initialStatusCode = DEFAULT_STATUS_CODE
}) => {
  const userDataFromCookie = getUserDataFromMemberCookie();
  const router = useRouter();

  const onRouteErrorHandler = useCallback<TRouteErrorCallback>((error, url) => {
    try {
      withScope(scope => {
        scope.setTag(`url`, url);
        captureException(
          error instanceof Error
            ? error
            : new Error(error ?? 'Unknown route change error (435534)')
        );
      });
    } catch (error) {
      // ignore
    }
  }, []);

  // manage using being signed in or not
  useEffect(() => {
    setUserId(userDataFromCookie?.id);
  }, [userDataFromCookie?.id]);

  const [statusCode, setStatusCode] = useStatusCode(
    initialStatusCode,
    onRouteErrorHandler
  );

  // track pageviews
  useEffect(() => {
    if (!statusCode) {
      return;
    }

    const queryParams = new URLSearchParams();
    queryParams.set('asPath', router.asPath);
    queryParams.set(
      'query',
      new URLSearchParams(router.query as Record<string, string>).toString()
    );
    let calculatedTrackingPath: string;
    if (trackingPath) {
      calculatedTrackingPath = trackingPath;
      queryParams.set('pathName', router.pathname);
    } else {
      calculatedTrackingPath = router.pathname;
    }

    trackPageview(`${calculatedTrackingPath}?${queryParams.toString()}`, {
      statusCode: statusCode,
      userIsSignedIn: userDataFromCookie.isSignedIn
    });

    setStatusCode(undefined);
  }, [
    statusCode,
    trackingPath,
    router.pathname,
    router.query,
    router.asPath,
    userDataFromCookie?.isSignedIn,
    setStatusCode
  ]);

  const isEnabled =
    (router.query?.trackingenabled ?? TRACKING_ENABLED_QUERY_PARAM_VALUE) ===
    TRACKING_ENABLED_QUERY_PARAM_VALUE;

  return (
    <>
      <Head>
        <script
          key="ga-init"
          dangerouslySetInnerHTML={{
            __html: `
            if (typeof window !== 'undefined') {
              window.ga = window.ga || function () {
                  (ga.q = ga.q || []).push(arguments);
                };
              ga.l = +new Date();
              window.ga('create', '${GA_MEASUREMENT_ID}', 'auto');
            }`
          }}
        ></script>
      </Head>
      {isEnabled && (
        <Script
          id="ga-lib"
          src="https://www.google-analytics.com/analytics.js"
          strategy="lazyOnload"
        />
      )}
    </>
  );
};
